// Sponsor / Contact Consistency — Chrona Review feature (A2F-1158).
//
// v1 scope: cover-letter contact drift only. Detects when later cover letters
// still name a previous FDA contact, against the current (most-recent) contact.
// Standalone (not in the consistency aggregate yet) and gated behind the dev
// shortcut (Ctrl+Shift+D) via <GatedRoute> until the corpus is verified.
//
// The scan is deterministic and returns the full result synchronously, so this
// page calls the scan endpoint directly (no task polling) and renders the
// current contact, the contact-change timeline, and per-finding cards with a
// 21 CFR Part 11 dismiss-with-rationale flow.

import { useCallback, useEffect, useState } from 'react';
import {
  MapPin, Mail, Phone, Check, CircleCheck, TriangleAlert, ArrowRight,
  Shield, Lock, User, FileText, RefreshCw, LoaderCircle, Undo2,
} from 'lucide-react';

import { useProject } from '@/lib/ProjectContext';
import { usePermissions } from '@/lib/usePermissions';
import { api } from '@/lib/api';
import type {
  SponsorConsistencyScanResponse,
  SponsorContactNode,
  SponsorFinding,
} from '@/types/api';

const MIN_RATIONALE = 10;

function fieldIcon(field: string) {
  if (field === 'contact_email') return Mail;
  if (field === 'contact_phone') return Phone;
  if (field === 'org_name' || field === 'address') return FileText;
  return User;
}

function fieldLabel(field: string): string {
  switch (field) {
    case 'contact_name': return 'Regulatory contact';
    case 'contact_email': return 'Contact email';
    case 'contact_phone': return 'Phone';
    case 'org_name': return 'Sponsor name';
    case 'address': return 'Address';
    default: return field;
  }
}

function initials(name: string | null): string {
  if (!name) return '—';
  return name.replace(/^Dr\.\s*/, '').split(/\s+/).map((s) => s[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();
}

/** Match a finding's stale value to a timeline node (for the "N later
 * documents still name this contact" marker) — comparing only the node
 * attribute that corresponds to the finding's field, normalized the same way
 * the backend compares (digits-only phone, lowercased email/name). Gating on
 * `finding.field` prevents cross-field misattribution (A2F-1235 #6): a name
 * finding whose value happens to coincide with a node's email no longer
 * attaches to — and inflates the count of — the wrong node. */
function nodeMatchesStaleValue(node: SponsorContactNode, finding: SponsorFinding): boolean {
  const text = (s: string | null) => (s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  const digits = (s: string | null) => (s ?? '').replace(/\D/g, '');
  const v = finding.staleValue;
  switch (finding.field) {
    case 'contact_name':
      return !!node.name && text(node.name) === text(v);
    case 'contact_email':
      return !!node.email && text(node.email) === text(v);
    case 'contact_phone':
      return !!node.phone && digits(node.phone).length > 0 && digits(node.phone) === digits(v);
    default:
      return false; // org_name / address aren't represented as contact nodes
  }
}

function staleDocCountForNode(node: SponsorContactNode, findings: SponsorFinding[]): number {
  const docs = new Set<string>();
  for (const f of findings) {
    if (f.dismissed) continue;
    if (nodeMatchesStaleValue(node, f)) {
      f.documents.forEach((d) => docs.add(d));
    }
  }
  return docs.size;
}

// ---------------------------------------------------------------------------

function CurrentContactBlock({ contact }: { contact: SponsorContactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand-orange">
        <MapPin className="w-3.5 h-3.5" /> Current sponsor contact
      </div>
      <div className="mt-3 flex flex-wrap items-start gap-x-8 gap-y-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-charcoal text-white flex items-center justify-center text-xs font-semibold">
            {initials(contact.name)}
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">{contact.name || 'Unknown'}</div>
            {contact.establishedDoc && (
              <div className="text-xs text-slate-500">established by {contact.establishedDoc}</div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1 text-sm text-slate-700">
          {contact.email && (
            <span className="inline-flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" />{contact.email}</span>
          )}
          {contact.phone && (
            <span className="inline-flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" />{contact.phone}</span>
          )}
        </div>
        {contact.establishedSequence != null && (
          <div className="text-xs text-slate-500">
            <span className="block uppercase tracking-wide">Established</span>
            <span className="text-slate-700">SN {String(contact.establishedSequence).padStart(4, '0')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ContactTimeline({ timeline, findings }: { timeline: SponsorContactNode[]; findings: SponsorFinding[] }) {
  if (timeline.length === 0) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="text-sm font-semibold text-slate-900">Contact history</div>
      <div className="text-xs text-slate-500">Who has been the named FDA contact over this IND&apos;s life</div>
      <div className="mt-4 flex flex-wrap items-stretch gap-2">
        {timeline.map((node, i) => {
          const staleCount = staleDocCountForNode(node, findings);
          return (
            <div key={node.id} className="flex items-stretch gap-2">
              <div className={`min-w-[180px] rounded-lg border p-3 ${node.isCurrent ? 'border-brand-orange/40 bg-orange-50' : staleCount > 0 ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${node.isCurrent ? 'bg-brand-orange text-white' : 'bg-slate-300 text-slate-700'}`}>
                    {node.isCurrent ? <Check className="w-3 h-3" /> : node.id}
                  </div>
                  <div className="text-sm font-semibold text-slate-900">{node.name || node.email || 'Unknown'}</div>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {node.isCurrent ? 'Current · ' : ''}
                  {node.establishedSequence != null ? `established SN ${String(node.establishedSequence).padStart(4, '0')}` : ''}
                </div>
                {staleCount > 0 && (
                  <div className="mt-2 inline-flex items-center gap-1 text-xs text-amber-700">
                    <TriangleAlert className="w-3 h-3" />
                    {staleCount} later document{staleCount > 1 ? 's' : ''} still name this contact
                  </div>
                )}
              </div>
              {i < timeline.length - 1 && (
                <div className="flex items-center text-slate-300"><ArrowRight className="w-4 h-4" /></div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FindingCard({
  finding, currentContact, canDismiss, onDismiss, onUndo,
}: {
  finding: SponsorFinding;
  currentContact: SponsorContactNode | null;
  canDismiss: boolean;
  onDismiss: (key: string, reason: string) => Promise<void>;
  onUndo: (key: string) => Promise<void>;
}) {
  const [dismissing, setDismissing] = useState(false);
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const Icon = fieldIcon(finding.field);
  const valid = rationale.trim().length >= MIN_RATIONALE;

  if (finding.dismissed) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600">
            <Shield className="w-3.5 h-3.5" /> Dismissed
          </span>
          <span className="text-xs text-slate-500">
            {fieldLabel(finding.field)} · {finding.documents.length} document{finding.documents.length > 1 ? 's' : ''} · recorded in audit trail
          </span>
          <div className="flex-1" />
          <button
            type="button"
            disabled={busy || !canDismiss}
            onClick={async () => { setBusy(true); try { await onUndo(finding.findingKey); } finally { setBusy(false); } }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            <Undo2 className="w-3.5 h-3.5" /> Reopen
          </button>
        </div>
        {finding.dismissReason && (
          <div className="mt-2 inline-flex items-start gap-2 text-xs text-slate-600">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span className="italic">&ldquo;{finding.dismissReason}&rdquo;</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-full px-2.5 py-1">
          <Icon className="w-3.5 h-3.5" /> {fieldLabel(finding.field)}
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Open
        </span>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
          <div className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700">
            <CircleCheck className="w-3.5 h-3.5" /> Current — authoritative
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-900 break-words">{finding.currentValue}</div>
          {finding.field.startsWith('contact_') && currentContact?.establishedSequence != null && (
            <div className="text-xs text-slate-500">established SN {String(currentContact.establishedSequence).padStart(4, '0')}</div>
          )}
        </div>
        <div className="hidden md:flex items-center text-slate-300"><ArrowRight className="w-4 h-4" /></div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700">
            <TriangleAlert className="w-3.5 h-3.5" /> Found in document — stale
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-900 break-words">{finding.staleValue}</div>
        </div>
      </div>

      <div className="mt-3">
        <div className="text-xs text-slate-500">
          Carried by {finding.documents.length} document{finding.documents.length > 1 ? 's' : ''}
        </div>
        <div className="mt-1 flex flex-col gap-1">
          {finding.documents.map((d) => (
            <div key={d} className="inline-flex items-center gap-2 text-xs text-slate-600">
              <FileText className="w-3.5 h-3.5 text-slate-400" /> {d}
            </div>
          ))}
        </div>
      </div>

      {dismissing ? (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-800">
            <Shield className="w-4 h-4" /> Dismiss this finding
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Provide a rationale. This is recorded as an attributable, time-stamped decision in the audit trail.
          </div>
          <textarea
            className="mt-2 w-full rounded-lg border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
            rows={3}
            placeholder="e.g. SN10 cover letter was superseded by an erratum naming the current contact; reference is no longer outgoing."
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            autoFocus
          />
          <div className="mt-2 flex items-center justify-between">
            <span className={`inline-flex items-center gap-1.5 text-xs ${valid ? 'text-green-700' : 'text-slate-500'}`}>
              <Lock className="w-3 h-3" />
              {valid ? '21 CFR Part 11 — attributable & time-stamped' : `Minimum ${MIN_RATIONALE} characters (${rationale.trim().length}/${MIN_RATIONALE})`}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setDismissing(false); setRationale(''); }}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!valid || busy}
                onClick={async () => {
                  setBusy(true);
                  try { await onDismiss(finding.findingKey, rationale.trim()); setDismissing(false); setRationale(''); }
                  finally { setBusy(false); }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <Shield className="w-3.5 h-3.5" /> Confirm dismiss
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            disabled={!canDismiss}
            title={canDismiss ? undefined : 'Requires Contributor role'}
            onClick={() => setDismissing(true)}
            className="px-3 py-1.5 text-xs font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            Dismiss…
          </button>
          <div className="flex-1" />
          <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
            <Lock className="w-3 h-3" /> Nothing is auto-changed
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function SponsorConsistencyPage() {
  const { activeProject } = useProject();
  const { canRunScans, canDismissFindings } = usePermissions();
  const projectId = activeProject?.id;

  const [result, setResult] = useState<SponsorConsistencyScanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false); // a scan has been run/cached
  const [error, setError] = useState<string | null>(null);

  // Read path — load the cached result without triggering a scan/write, so a
  // viewer can view results and navigating here never POSTs (review H1/H2).
  const loadCached = useCallback(async () => {
    if (!projectId) { setLoading(false); return; }
    try {
      const r = await api.sponsorConsistency.get(projectId);
      setResult(r);
      setScanned(r != null);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Write path — only via the explicit, canRunScans-gated button.
  const runScan = useCallback(async () => {
    if (!projectId) return;
    setScanning(true);
    setError(null);
    try {
      const r = await api.sponsorConsistency.scan(projectId);
      setResult(r);
      setScanned(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scan failed');
    } finally {
      setScanning(false);
    }
  }, [projectId]);

  useEffect(() => { void loadCached(); }, [loadCached]);

  // Dismiss/undismiss write the dismissal table; re-read (GET) re-applies
  // merge_dismissals — no re-scan needed.
  const dismiss = useCallback(async (key: string, reason: string) => {
    if (!projectId) return;
    await api.sponsorConsistency.dismiss(projectId, key, reason);
    await loadCached();
  }, [projectId, loadCached]);

  const undo = useCallback(async (key: string) => {
    if (!projectId) return;
    await api.sponsorConsistency.undismiss(projectId, key);
    await loadCached();
  }, [projectId, loadCached]);

  if (!activeProject) {
    return <div className="p-8 text-sm text-slate-500">Select a project to run Sponsor Contact Consistency.</div>;
  }

  const findings = result?.findings ?? [];
  const openFindings = findings.filter((f) => !f.dismissed);
  const dismissedFindings = findings.filter((f) => f.dismissed);
  const isClean = result != null && findings.length === 0;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-brand-orange">Chrona Review</div>
          <h1 className="text-2xl font-semibold text-brand-charcoal">Contacts</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-xl">
            Detects when later cover letters still name a previous FDA contact. A stale contact is a real
            correspondence risk — this surfaces it for your review.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {scanning ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
              <LoaderCircle className="w-3.5 h-3.5 animate-spin" /> Scanning…
            </span>
          ) : scanned ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700">
              <Check className="w-3.5 h-3.5" /> Scan complete
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => void runScan()}
            disabled={!canRunScans || scanning || loading}
            title={!canRunScans ? 'Requires Contributor role' : undefined}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-brand-orange text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors ${!canRunScans || scanning || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} /> {scanning ? 'Scanning…' : scanned ? 'Re-scan' : 'Scan'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {result?.errors && result.errors.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <div className="inline-flex items-center gap-1.5 font-medium">
            <TriangleAlert className="w-4 h-4" />
            {result.errors.length} sponsor document{result.errors.length > 1 ? 's' : ''} could not be read
          </div>
          <div className="mt-1 text-xs text-amber-700">
            These were skipped — contact findings may be incomplete. Re-scan once the file is readable.
          </div>
          <ul className="mt-2 space-y-1">
            {result.errors.map((e) => (
              <li key={e.document} className="inline-flex items-start gap-2 text-xs text-amber-700">
                <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span><b>{e.document}</b> — {e.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <LoaderCircle className="w-6 h-6 mx-auto animate-spin text-brand-orange" />
          <div className="mt-2 text-sm text-slate-500">Loading…</div>
        </div>
      ) : !result ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <Shield className="w-7 h-7 mx-auto text-slate-300" />
          <div className="mt-2 text-sm font-semibold text-slate-800">No scan has been run yet</div>
          <div className="mt-1 text-xs text-slate-500">
            {canRunScans
              ? 'Run a scan to check cover-letter contact consistency across this IND.'
              : 'Ask a contributor to run a scan.'}
          </div>
          {canRunScans && (
            <button
              type="button"
              onClick={() => void runScan()}
              disabled={scanning}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-brand-orange text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} /> {scanning ? 'Scanning…' : 'Run scan'}
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Always-present reference points */}
          {result.currentContact && <CurrentContactBlock contact={result.currentContact} />}
          <ContactTimeline timeline={result.contactTimeline} findings={findings} />

          {isClean ? (
            <div className="rounded-xl border border-green-200 bg-green-50 p-8 text-center">
              <Shield className="w-7 h-7 mx-auto text-green-600" />
              <div className="mt-2 text-sm font-semibold text-slate-800">Every submission agrees on the current sponsor contact</div>
              <div className="mt-1 text-xs text-slate-600">
                {result.currentContact?.name
                  ? <>All cover letters name <b>{result.currentContact.name}</b> as the FDA contact. No drift detected.</>
                  : 'No contact drift detected across the scanned cover letters.'}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                {result.summary.coverLettersScanned ?? 0} cover letters checked
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">
                  {openFindings.length > 0 ? `${openFindings.length} open finding${openFindings.length > 1 ? 's' : ''}` : 'No open findings'}
                </span>
                {dismissedFindings.length > 0 && (
                  <span className="text-xs text-slate-500">{dismissedFindings.length} dismissed · recorded in audit trail</span>
                )}
              </div>
              {openFindings.length === 0 && dismissedFindings.length > 0 && (
                <div className="inline-flex items-center gap-2 text-sm text-slate-600">
                  <Shield className="w-4 h-4" /> All findings have been reviewed and dismissed with rationale.
                </div>
              )}
              {[...openFindings, ...dismissedFindings].map((f) => (
                <FindingCard
                  key={f.findingKey}
                  finding={f}
                  currentContact={result.currentContact}
                  canDismiss={canDismissFindings}
                  onDismiss={dismiss}
                  onUndo={undo}
                />
              ))}
            </div>
          )}
        </>
      )}

      <div className="text-xs text-slate-400 border-t border-slate-100 pt-3">
        v1 scope: cover-letter contact drift only. Coming in a later phase: Form 1571 &amp; Section 1.3.1 contact reconciliation.
      </div>
    </div>
  );
}
